import { message } from "antd";
import { useEffect, useState, useContext } from "react";
import {
  Project,
  SessionsWeek,
  TimeTrackingSession,
  UseTimeTrackingUserData,
} from "./types";
import { ApiClientContext } from "../../../App";

//this hook is used for handling of user sessions, projects, tasks, etc
export const useTimeTrackingUserData: UseTimeTrackingUserData = () => {
  const [isGettingInitialData, setIsGettingInitialData] =
    useState<boolean>(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [sessionWeeks, setSessionWeeks] = useState<SessionsWeek[]>([]);
  const [weeksToFetch, setWeeksToFetch] = useState<number>(4);
  const [sessionToDelete, setSessionToDelete] = useState<string>("");
  const [sessionToClone, setSessionToClone] = useState<string>("");

  const { apiClient } = useContext(ApiClientContext);

  const getInitialData = async () => {
    try {
      setIsGettingInitialData(true);
      const getProjects = apiClient.get("/api/projects/user");
      const getSessions = apiClient.get(
        `/api/time-tracking/history/${weeksToFetch}`,
      );

      const [projectsRes, sessionsRes] = await Promise.all([
        getProjects,
        getSessions,
      ]);
      setProjects(projectsRes.data.response.projects);
      setSessionWeeks(sessionsRes.data);

      setIsGettingInitialData(false);
    } catch (err: any) {
      message.error(
        "Could not get user data for tracker. " + err?.response?.data?.message,
      );
    }
  };

  const updateSessionWeeks = async () => {
    try {
      const res = await apiClient.get(
        `/api/time-tracking/history/${weeksToFetch}`,
      );

      setSessionWeeks(res.data as SessionsWeek[]);
    } catch (err) {
      message.error("Could not fetch sessions.");
    }
  };

  const cloneSession = async (
    session: TimeTrackingSession,
    projectId: string,
  ) => {
    setSessionToClone(session.id);
    await apiClient
      .post(`/api/time-tracking/sessions/create`, {
        start: session.start,
        end: session.end,
        projectId: projectId,
        description: session.description,
      })
      .then(async () => {
        await updateSessionWeeks();
        message.success("Session cloned successfully.");
      })
      .catch((error: any) => {
        message.error("Could not clone session.");
      })
      .finally(() => {
        setSessionToClone("");
      });
  };
  const deleteSession = async (sessionId: string) => {
    setSessionToDelete(sessionId);
    await apiClient
      .delete(`/api/time-tracking/sessions/delete/${sessionId}`)
      .then(async () => {
        await updateSessionWeeks();
        message.success("Session deleted successfully.");
      })
      .catch((error: any) => {
        message.error("Could not delete session.");
      })
      .finally(() => {
        setSessionToDelete("");
      });
  };

  useEffect(() => {
    getInitialData();
  }, [weeksToFetch]);

  return {
    projects,
    sessionWeeks,
    isGettingInitialData,
    weeksToFetch,
    setWeeksToFetch,
    updateSessionWeeks,
    cloneSession,
    deleteSession,
    sessionToDelete,
    sessionToClone,
  };
};
